// Implements: F-ITK-007, F-UIX-010
/**
 * The person's linked numbers, masked, each with its removal. Pure presentation.
 *
 * A row is the mockup's "connected" card line: a round message glyph, the masked number, where it
 * creates and since when, a status badge and the removal. It is keyed by its binding id (checklist
 * 14), and its button carries the masked number in its accessible name: three rows of "Desvincular"
 * read alike to a screen reader. The number is only ever the server's masked form; the client never
 * receives it in clear. The server serves no message history, so the mockup's history table is not
 * drawn.
 */

import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Badge } from "../../../shared/ui/atoms/Badge";
import { Button } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { EmptyState } from "../../../shared/ui/molecules/EmptyState";
import styles from "./WhatsApp.module.css";

export interface BindingRow {
  readonly id: string;
  readonly maskedAddress: string;
  /** Where the number creates, already written for a person. */
  readonly scope: string;
  readonly linkedOn: string;
  /** The machine-readable instant. */
  readonly createdAt: string;
}

export interface BindingListProps {
  readonly rows: readonly BindingRow[];
  readonly disabled: boolean;
  readonly onUnlink: (row: BindingRow) => void;
  readonly onRefresh: () => void;
  readonly refreshing: boolean;
}

export function BindingList({ rows, disabled, onUnlink, onRefresh, refreshing }: BindingListProps): ReactElement {
  const { t } = useTranslation();

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <h2 className={styles.subtitle}>{t("intake.list.title")}</h2>
        <Button variant="ghost" size="sm" loading={refreshing} onClick={onRefresh}>
          {t("intake.list.refresh")}
        </Button>
      </div>
      {rows.length === 0 ? (
        <EmptyState icon="message" title={t("intake.list.title")} body={t("intake.list.empty")} headingLevel={3} />
      ) : (
        <ul className={styles.list} aria-label={t("intake.list.title")}>
          {rows.map((row) => (
            <li key={row.id} className={styles.item}>
              <span className={styles.glyph}>
                <Icon name="message" />
              </span>
              <div className={styles.who}>
                <span className={styles.number}>{row.maskedAddress}</span>
                <span>{row.scope}</span>
                <time dateTime={row.createdAt}>{t("intake.list.linkedOn", { date: row.linkedOn })}</time>
              </div>
              <Badge tone="success">
                <Icon name="check-circle" size="sm" />
                {t("intake.list.badge")}
              </Badge>
              <Button
                variant="danger"
                size="sm"
                disabled={disabled}
                label={t("intake.unlink.for", { number: row.maskedAddress })}
                onClick={() => {
                  onUnlink(row);
                }}
              >
                {t("intake.unlink.action")}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
